import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AdminTopline, Panel } from "../../components/console";
import { api, authHeaders, describeError } from "../../lib/api";
import type { User } from "../../lib/api";
import { getCurrentRole } from "../../lib/auth";
import { ROLE_LABEL_LO, canonicalRole, isConsoleRole, roleBadgeClass } from "../../lib/roles";
import { num, shortDate } from "../../lib/format";
import { positionLabel } from "../../lib/positions";

type RoleFilter = "all" | "citizen" | "officer" | "district_finance" | "province_oversight" | "super_admin";

/**
 * ຜູ້ໃຊ້ທົ່ວລະບົບ — รายชื่อ **ทุกบัญชีรวมประชาชน** ของผู้ดูแลระบบ
 *
 * ต่างจาก `/district/staff` ที่เป็นบัญชีหลังบ้านในเขตของตัวเองและแก้ไขได้:
 * จอนี้เป็น read-only ข้ามทั้งระบบ การแก้บัญชีต้องทำจากจอของระดับที่ดูแลคนนั้นอยู่
 * เพื่อให้ผ่านกฎ `manageableRoles` และเหลือร่องรอยใน audit trail ตามสายบังคับบัญชา
 */
async function fetchUsers(): Promise<User[]> {
  // เพดานเดียวกับที่ API จำกัดไว้ (maxUserPageLimit) — ระบบยังไม่ถึงหลักพันบัญชี
  // จึงยังไม่ต้องทำหน้าเลื่อนหน้า เหมือน RequestsPage
  const { data, error, response } = await api.GET("/v1/admin/users", {
    params: { query: { limit: 200 } },
    headers: authHeaders(),
  });
  if (error || !data) throw new Error(describeError(error, response.status));
  // list เก็บ tombstone (deleted_at != null) ไว้ให้ sync ได้ — หน้านี้ไม่แสดงบัญชีที่ถูกลบ
  return data.items.filter((u) => u.deleted_at === null);
}

export function SystemUsersPage() {
  const role = getCurrentRole();
  const list = useQuery({ queryKey: ["admin-users"], queryFn: fetchUsers });
  const [roleFilter, setRoleFilter] = useState<RoleFilter>("all");
  const [q, setQ] = useState("");

  const items = useMemo(() => list.data ?? [], [list.data]);

  const filtered = useMemo(() => {
    let out = items;
    if (roleFilter !== "all") out = out.filter((u) => canonicalRole(u.role) === roleFilter);
    const needle = q.trim().toLowerCase();
    if (needle) {
      out = out.filter((u) =>
        [u.full_name, u.phone, u.email].filter(Boolean).some((v) => v?.toLowerCase().includes(needle)),
      );
    }
    return out;
  }, [items, roleFilter, q]);

  const staffCount = items.filter((u) => isConsoleRole(u.role)).length;

  return (
    <div>
      <AdminTopline
        title="ຜູ້ໃຊ້ທົ່ວລະບົບ"
        subtitle="ອ່ານໄດ້ຢ່າງດຽວ — ການແກ້ໄຂບັນຊີເຮັດຈາກໜ້າຈັດການພະນັກງານຂອງແຕ່ລະລະດັບ"
        roleLabel={role ? ROLE_LABEL_LO[role] : undefined}
      />

      {list.isError ? (
        <div className="alert-error" style={{ marginBottom: "var(--space-4)" }}>
          ໂຫຼດຂໍ້ມູນບໍ່ສຳເລັດ: {list.error.message}{" "}
          <button
            type="button"
            className="btn-outline btn-sm"
            onClick={() => {
              void list.refetch();
            }}
          >
            ລອງໃໝ່
          </button>
        </div>
      ) : null}

      <div className="kpi-grid" style={{ gridTemplateColumns: "repeat(2, 1fr)" }}>
        <article className="kpi">
          <div className="lbl">ບັນຊີທັງໝົດ</div>
          <div className="val num">{list.isPending ? "—" : num(items.length)}</div>
          <div className="delta" style={{ color: "var(--ink-400)" }}>
            ບໍ່ນັບບັນຊີທີ່ຖືກປິດແລ້ວ
          </div>
        </article>
        <article className="kpi">
          <div className="lbl">ບັນຊີຫຼັງບ້ານ</div>
          <div className="val num">{list.isPending ? "—" : num(staffCount)}</div>
          <div className="delta" style={{ color: "var(--ink-400)" }}>
            ຕາແສງ · ການເງິນ · ຜູ້ບໍລິຫານ
          </div>
        </article>
      </div>

      <Panel title="ລາຍຊື່ບັນຊີ">
        <div className="filter-bar" style={{ marginBottom: "var(--space-4)" }}>
          <input
            className="filter-search"
            style={{ maxWidth: "20rem" }}
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
            }}
            placeholder="ຄົ້ນຫາ ຊື່ / ເບີໂທ / ອີເມວ"
          />
          <select
            className="filter-select"
            value={roleFilter}
            onChange={(e) => {
              setRoleFilter(e.target.value as RoleFilter);
            }}
            aria-label="ຕົວກອງບົດບາດ"
          >
            <option value="all">ທຸກບົດບາດ</option>
            <option value="citizen">{ROLE_LABEL_LO.citizen}</option>
            <option value="officer">{ROLE_LABEL_LO.officer}</option>
            <option value="district_finance">{ROLE_LABEL_LO.district_finance}</option>
            <option value="province_oversight">{ROLE_LABEL_LO.province_oversight}</option>
            <option value="super_admin">{ROLE_LABEL_LO.super_admin}</option>
          </select>
        </div>

        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th>ຊື່</th>
                <th>ຕິດຕໍ່</th>
                <th>ບົດບາດ</th>
                <th>ສະໝັກເມື່ອ</th>
              </tr>
            </thead>
            <tbody>
              {list.isPending ? (
                <tr>
                  <td colSpan={4} className="data-table__empty">
                    ກຳລັງໂຫຼດ…
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={4} className="data-table__empty">
                    ບໍ່ມີຜູ້ໃຊ້ທີ່ກົງກັບຕົວກອງນີ້
                  </td>
                </tr>
              ) : (
                filtered.map((u) => (
                  <tr key={u.id}>
                    <td style={{ fontWeight: 500 }}>{u.full_name}</td>
                    <td>
                      {u.phone ? <div className="body-sm num">{u.phone}</div> : null}
                      {u.email ? <div className="caption">{u.email}</div> : null}
                      {!u.phone && !u.email ? <span className="caption">—</span> : null}
                    </td>
                    <td>
                      <span className={roleBadgeClass(u.role)}>{ROLE_LABEL_LO[u.role]}</span>
                      {u.position_code !== "CIT" ? <div className="caption">{positionLabel(u.position_code)}</div> : null}
                    </td>
                    <td className="caption num">{shortDate(u.created_at)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}
